/**
 * SkiFree Yeti DO - FPV Camera Rig
 * Provides downhill forward skiing camera with tilt/inertia, dynamic FOV,
 * screen shake on collision/knockdowns, CS:GO/Fortnite weapon screen recoil,
 * and 180° rearview precision rifle aiming.
 */

import { Scene, UniversalCamera, Vector3, Scalar } from "@babylonjs/core";

export type CameraMode = 'third_person' | 'first_person';

export class CameraRig {
  public camera: UniversalCamera;
  private scene: Scene;

  // Camera Perspective Mode (Diablo-style Third-Person Overhead by default)
  public cameraMode: CameraMode = 'third_person';

  // State
  public isAimingRear: boolean = false;
  private currentYaw: number = Math.PI;
  private targetYaw: number = Math.PI;
  private currentRoll: number = 0;
  private targetRoll: number = 0;
  private baseFov: number = 0.85; // rad (~48 deg)
  private currentSpeed: number = 0;
  private shakeTrauma: number = 0;
  public aimYawOffset: number = 0;
  public aimPitchOffset: number = 0;

  // CS:GO / Fortnite Screen Recoil Dynamics
  private recoilPitch: number = 0;
  private recoilYaw: number = 0;

  // Yeti Target Auto-Lock & Auto-Aim Framing
  public isTargetLocked: boolean = false;
  public autoLockEnabled: boolean = true;

  // End-of-Level Cinematic Cutscene State
  public isCutsceneActive: boolean = false;
  public cutscenePhase: 'takedown_orbit' | 'victory_pass' | 'summit_pullout' | null = null;
  public cutsceneTimer: number = 0;
  public cutsceneFocusPos: Vector3 = new Vector3(0, 0, 0);
  public cutscenePlayerPos: Vector3 = new Vector3(0, 0, 0);

  constructor(scene: Scene, canvas: HTMLCanvasElement) {
    this.scene = scene;
    this.camera = new UniversalCamera("fpvCamera", new Vector3(0, 1.6, 0), this.scene);
    this.camera.setTarget(new Vector3(0, 1.4, -20)); // Looking downhill along -Z
    this.camera.fov = this.baseFov;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 1200;

    // Attach mouse controls for fine rifle aiming
    this.camera.inputs.clear(); // Custom control handling
    this.setupInputListeners(canvas);
  }

  private setupInputListeners(canvas: HTMLCanvasElement): void {
    canvas.addEventListener("click", () => {
      if (document.pointerLockElement !== canvas) {
        canvas.requestPointerLock?.();
      }
    });

    window.addEventListener("mousemove", (e: MouseEvent) => {
      if (document.pointerLockElement === canvas) {
        const sens = 0.0018;
        this.aimYawOffset = Scalar.Clamp(this.aimYawOffset - e.movementX * sens, -0.60, 0.60);
        this.aimPitchOffset = Scalar.Clamp(this.aimPitchOffset - e.movementY * sens, -0.35, 0.40);
      }
    });

    window.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        this.isAimingRear = true;
      }
    });

    window.addEventListener("keyup", (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        this.isAimingRear = false;
      }
    });

    canvas.addEventListener("mousedown", (e: MouseEvent) => {
      if (e.button === 2) { // Right-click aim
        this.isAimingRear = true;
        e.preventDefault();
      }
    });

    canvas.addEventListener("mouseup", (e: MouseEvent) => {
      if (e.button === 2) {
        this.isAimingRear = false;
        e.preventDefault();
      }
    });

    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  public setRearview(active: boolean): void {
    this.isAimingRear = active;
  }

  public applyAimDelta(deltaYaw: number, deltaPitch: number): void {
    this.aimYawOffset = Scalar.Clamp(this.aimYawOffset + deltaYaw, -0.60, 0.60);
    this.aimPitchOffset = Scalar.Clamp(this.aimPitchOffset + deltaPitch, -0.35, 0.40);
  }

  public toggleCameraMode(): CameraMode {
    this.cameraMode = this.cameraMode === 'third_person' ? 'first_person' : 'third_person';
    return this.cameraMode;
  }

  public setCameraMode(mode: CameraMode): void {
    this.cameraMode = mode;
  }

  public addImpactShake(amount: number = 1.0): void {
    this.shakeTrauma = Math.min(1.0, this.shakeTrauma + amount);
  }

  /**
   * Weapon Screen Recoil Impulse:
   * Instant upward kick with slight random horizontal deviation,
   * dampening back smoothly via spring-damper lerp.
   */
  public triggerRecoil(pitchKick: number = 0.08, yawKick: number = 0.025): void {
    this.recoilPitch += pitchKick;
    this.recoilYaw += (Math.random() - 0.5) * yawKick;
  }

  public startCutscene(
    phase: 'takedown_orbit' | 'victory_pass' | 'summit_pullout',
    focusPos: Vector3,
    playerPos: Vector3
  ): void {
    this.isCutsceneActive = true;
    this.cutscenePhase = phase;
    this.cutsceneTimer = 0;
    this.cutsceneFocusPos.copyFrom(focusPos);
    this.cutscenePlayerPos.copyFrom(playerPos);
  }

  public setCutscenePhase(phase: 'takedown_orbit' | 'victory_pass' | 'summit_pullout'): void {
    this.cutscenePhase = phase;
    this.cutsceneTimer = 0;
  }

  public stopCutscene(): void {
    this.isCutsceneActive = false;
    this.cutscenePhase = null;
    this.cutsceneTimer = 0;
  }

  public triggerNearMiss(): void {
    this.addImpactShake(0.65);
  }

  public update(
    playerPosition: Vector3,
    steerInput: number,
    speedMph: number,
    deltaTime: number,
    slopePitchRad: number = 0,
    targetPos?: Vector3,
    isTargetActive?: boolean,
    isTucking: boolean = false
  ): void {
    this.currentSpeed = speedMph;

    // 0. Recoil Decay (Spring recovery)
    if (Math.abs(this.recoilPitch) > 0.0005 || Math.abs(this.recoilYaw) > 0.0005) {
      this.recoilPitch = Scalar.Lerp(this.recoilPitch, 0, Math.min(1.0, deltaTime * 16.0));
      this.recoilYaw = Scalar.Lerp(this.recoilYaw, 0, Math.min(1.0, deltaTime * 16.0));
    }

    // 0b. Cinematic Cutscene Execution Mode
    if (this.isCutsceneActive) {
      this.cutsceneTimer += deltaTime;
      if (this.cutscenePhase === 'takedown_orbit') {
        // Dramatic bullet-time orbit sweeping around the downed Yeti
        const orbitRadius = 9.8;
        const orbitSpeed = 0.95;
        const angle = this.cutsceneTimer * orbitSpeed;
        const camX = this.cutsceneFocusPos.x + Math.sin(angle) * orbitRadius;
        const camY = this.cutsceneFocusPos.y + 3.4;
        const camZ = this.cutsceneFocusPos.z + Math.cos(angle) * orbitRadius;
        this.camera.position.set(camX, camY, camZ);
        this.camera.setTarget(new Vector3(this.cutsceneFocusPos.x, this.cutsceneFocusPos.y + 1.2, this.cutsceneFocusPos.z));
      } else if (this.cutscenePhase === 'victory_pass') {
        // Dynamic low-angle 3/4 tracking shot framing skier triumphantly blasting past
        const targetX = playerPosition.x + 3.2;
        const targetY = playerPosition.y + 1.35;
        const targetZ = playerPosition.z - 2.8;
        this.camera.position.x = Scalar.Lerp(this.camera.position.x, targetX, Math.min(1.0, deltaTime * 10.0));
        this.camera.position.y = Scalar.Lerp(this.camera.position.y, targetY, Math.min(1.0, deltaTime * 10.0));
        this.camera.position.z = Scalar.Lerp(this.camera.position.z, targetZ, Math.min(1.0, deltaTime * 10.0));
        this.camera.setTarget(new Vector3(playerPosition.x, playerPosition.y + 1.1, playerPosition.z));
      } else if (this.cutscenePhase === 'summit_pullout') {
        // Panoramic mountain pull-back
        const targetX = playerPosition.x;
        const targetY = playerPosition.y + 8.5;
        const targetZ = playerPosition.z + 16.0;
        this.camera.position.x = Scalar.Lerp(this.camera.position.x, targetX, Math.min(1.0, deltaTime * 3.5));
        this.camera.position.y = Scalar.Lerp(this.camera.position.y, targetY, Math.min(1.0, deltaTime * 3.5));
        this.camera.position.z = Scalar.Lerp(this.camera.position.z, targetZ, Math.min(1.0, deltaTime * 3.5));
        this.camera.setTarget(new Vector3(playerPosition.x, playerPosition.y + 0.8, playerPosition.z - 28.0));
      }
      return;
    }

    // 1. Position camera based on cameraMode
    const isThirdPerson = this.cameraMode === 'third_person';
    const speedRatio = Math.min(1.8, speedMph / 45);
    const mogulChatter = Math.sin(Date.now() * 0.024) * 0.012 * speedRatio;

    if (isThirdPerson) {
      // Elevated downhill perspective framing skier in lower third with expansive slope visibility
      const speedPullBack = (speedMph / 60) * 0.45;
      const followDist = 4.85 + speedPullBack;
      const tuckDrop = isTucking ? 0.72 : 0;
      const followHeight = 2.85 - tuckDrop;
      const targetX = playerPosition.x + (this.isAimingRear ? 0 : this.aimYawOffset * 1.8);
      const targetY = playerPosition.y + followHeight + mogulChatter * 0.2;
      const targetZ = playerPosition.z + (this.isAimingRear ? -followDist : followDist);

      this.camera.position.x = Scalar.Lerp(this.camera.position.x, targetX, Math.min(1.0, deltaTime * 14.0));
      this.camera.position.y = Scalar.Lerp(this.camera.position.y, targetY, Math.min(1.0, deltaTime * 12.0));
      this.camera.position.z = Scalar.Lerp(this.camera.position.z, targetZ, Math.min(1.0, deltaTime * 14.0));
    } else {
      // Classic FPV eye level with aerodynamic tuck drop
      const eyeHeight = isTucking ? 1.05 : 1.55;
      const targetY = playerPosition.y + eyeHeight + mogulChatter;
      this.camera.position.x = Scalar.Lerp(this.camera.position.x, playerPosition.x, Math.min(1.0, deltaTime * 16.0));
      this.camera.position.y = Scalar.Lerp(this.camera.position.y, targetY, Math.min(1.0, deltaTime * 14.0));
      this.camera.position.z = playerPosition.z;
    }

    // Harmonic Screen Shake on Impact / Knockdowns (Zero high-frequency strobe)
    let shakeX = 0;
    let shakeY = 0;
    let shakeRoll = 0;
    if (this.shakeTrauma > 0.005) {
      const shakePower = this.shakeTrauma * this.shakeTrauma;
      const t = performance.now() * 0.001;
      shakeX = Math.sin(t * 38.0) * 0.18 * shakePower;
      shakeY = Math.cos(t * 46.0) * 0.14 * shakePower;
      shakeRoll = Math.sin(t * 26.0) * 0.035 * shakePower;
      this.shakeTrauma = Math.max(0, this.shakeTrauma - deltaTime * 2.8);
    }
    this.camera.position.x += shakeX;
    this.camera.position.y += shakeY;

    // 2. Camera Bank / Roll on carving
    if (isThirdPerson) {
      this.targetRoll = -steerInput * 0.08; // Dynamic bank to keep horizon kinetic (~5 deg)
    } else {
      this.targetRoll = -steerInput * 0.22; // Visceral FPV bank into turn (~13 deg)
    }
    this.currentRoll = Scalar.Lerp(this.currentRoll, this.targetRoll, Math.min(1.0, deltaTime * 10.0));
    this.camera.rotation.z = this.currentRoll + shakeRoll;

    // 3. Dynamic FOV based on downhill speed (60 deg [1.05 rad] -> 88 deg [1.54 rad])
    const fovRatio = Math.max(0, Math.min(1.0, (speedMph - 20) / 60));
    const targetFov = 1.05 + fovRatio * 0.49;
    this.camera.fov = Scalar.Lerp(this.camera.fov, targetFov, Math.min(1.0, deltaTime * 8.0));

    // 4. Yeti Auto-Lock & Target Framing (Upper Torso/Head vs Scenic Horizon)
    const carvingYawOffset = -steerInput * 0.09;
    const defaultYaw = (this.isAimingRear ? 0 : Math.PI) + (isThirdPerson ? carvingYawOffset * 0.4 : carvingYawOffset);

    let targetPitchAngle = 0;
    let targetYawAngle = defaultYaw;

    const hasValidTarget = !!(
      this.autoLockEnabled &&
      isTargetActive &&
      targetPos &&
      Math.abs(targetPos.z - playerPosition.z) > 1.0 &&
      Math.hypot(targetPos.x - playerPosition.x, targetPos.z - playerPosition.z) < 180
    );

    if (hasValidTarget && targetPos) {
      this.isTargetLocked = true;
      // Target Yeti upper torso / head (2.2m above ground surface)
      const targetCenterY = targetPos.y + 2.2;
      const dx = targetPos.x - this.camera.position.x;
      const dy = targetCenterY - this.camera.position.y;
      const dz = targetPos.z - this.camera.position.z;
      const distXZ = Math.hypot(dx, dz);

      // Pitch calculation: Looking UP is negative rotation.x, looking DOWN is positive
      const idealPitch = -Math.atan2(dy, distXZ) - 0.02;

      // Yaw calculation: Angle to target in world coordinates
      const idealYaw = Math.atan2(dx, dz);

      targetPitchAngle = idealPitch + this.aimPitchOffset * 0.4;
      targetYawAngle = idealYaw + this.aimYawOffset * 0.4;
    } else {
      this.isTargetLocked = false;
      if (isThirdPerson) {
        // Downhill slope pitch tilt (~10 degrees downward) looking ahead down the run
        const speedPitchCompression = (speedMph / 80) * 0.02;
        const basePitch = this.isAimingRear
          ? -0.02 + this.aimPitchOffset * 0.3
          : (0.17 + slopePitchRad * 0.22 + speedPitchCompression + this.aimPitchOffset * 0.3);
        targetPitchAngle = basePitch;
        targetYawAngle = defaultYaw + this.aimYawOffset;
      } else {
        // Natural downhill horizon framing (level eye-line, never looking down at skis)
        const speedPitchCompression = (speedMph / 80) * 0.03;
        const basePitch = this.isAimingRear 
          ? -0.02 + this.aimPitchOffset 
          : (0.01 + slopePitchRad * 0.12 + speedPitchCompression + this.aimPitchOffset);

        targetPitchAngle = basePitch;
        targetYawAngle = defaultYaw + this.aimYawOffset;
      }
    }

    // Shortest-path yaw lerp (prevents 360 wrap flickers)
    let yawDiff = targetYawAngle - this.currentYaw;
    while (yawDiff < -Math.PI) yawDiff += Math.PI * 2;
    while (yawDiff > Math.PI) yawDiff -= Math.PI * 2;
    this.currentYaw += yawDiff * Math.min(1.0, deltaTime * (this.isTargetLocked ? 14.0 : 12.0));
    this.camera.rotation.y = this.currentYaw + this.recoilYaw;

    // Pitch lerp (kicks upward on recoil)
    this.camera.rotation.x = Scalar.Lerp(
      this.camera.rotation.x,
      targetPitchAngle - this.recoilPitch,
      Math.min(1.0, deltaTime * (this.isTargetLocked ? 14.0 : 10.0))
    );
  }

  public getForwardRay(): Vector3 {
    return this.camera.getForwardRay().direction;
  }
}
